// Social and Creative - job cards (step-by-step instructions), keyed by job code. (S cards and F1)
// A step is { text, tag }; a red STOP bar is { stop }. Loaded automatically by constants/jobCards.js.

export const division = "Social and Creative";

export const cards = {
  S1: {
    title: "Single post (static)",
    before: [
      "The post is on an approved calendar",
      "Brief: theme, message, platform, what you want people to do",
    ],
    steps: [
      { text: "Check the brand guidelines and the last few posts so you do not repeat", tag: "Checked" },
      { text: "Write the copy: first line hook, body, what to do next", tag: "Copy" },
      { stop: "Division Lead or named project lead approves the copy" },
      { text: "Design it using the approved template and the right size", tag: "Design" },
      { text: "Check it yourself: spelling, brand colours, logo space, size, safe edges, link works", tag: "Self check done" },
      { stop: "Division Lead or named project lead approves the design" },
      { stop: "Client approves — at least ONE WEEK before it posts" },
      { text: "Put the copy and the design together into the 04 Approved folder", tag: "In 04 Approved" },
    ],
    finishedWhen: "Copy and design are paired in the 04 Approved folder. Nothing else can be published.",
  },
  S2: {
    title: "Carousel",
    before: [
      "The post is on an approved calendar",
      "You know the story it should tell",
    ],
    steps: [
      { text: "Write the slide order in TEXT first: hook slide, the points, the payoff, the last slide", tag: "Slide plan" },
      { stop: "Division Lead or named project lead approves the slide order" },
      { text: "Write the words for each slide, to fit the space", tag: "Copy" },
      { stop: "Division Lead or named project lead approves the copy" },
      { text: "Design ONLY slide 1 and ONE middle slide", tag: "2 sample slides" },
      { stop: "Division Lead or named project lead approves those 2 before the rest is built" },
      { text: "Build the remaining slides the same way", tag: "Full carousel" },
      { text: "Check: slide order, same margins, readable when small, export order correct", tag: "Checked" },
      { stop: "Client approves" },
      { text: "Move to the 04 Approved folder", tag: "In 04 Approved" },
    ],
    finishedWhen: "All slides approved and in the 04 Approved folder, in the right order.",
  },
  S3: {
    title: "LinkedIn post",
    before: [
      "The post is on the approved LinkedIn calendar",
      "You know WHO is writing the copy: us, or Growth Systems",
    ],
    steps: [
      { text: "Confirm at calendar stage who writes the copy. Not knowing this is why posts sit unpublished", tag: "Owner known" },
      { text: "Get or write the copy. First 2 lines matter most — LinkedIn hides the rest", tag: "Copy" },
      { stop: "Division Lead or named project lead approves the copy" },
      { text: "Design: one image, or a document you export as PDF", tag: "Design" },
      { text: "Check: readable in the feed, right size, PDF pages in order, slide 1 has the hook", tag: "Checked" },
      { stop: "Client approves" },
      { text: "Move to 04 Approved, then publish", tag: "Published" },
      { text: "Tell Growth Systems it is live so they can reply to early comments", tag: "Team told" },
    ],
    finishedWhen: "Post is live and Growth Systems has been told, so the first hour is used.",
  },
  S4: {
    title: "Stories",
    before: [
      "A slot on the calendar, or a live moment the Division Lead has approved",
    ],
    steps: [
      { text: "Decide what it is for: awareness, a poll, behind the scenes, a link, or pushing a feed post", tag: "Purpose" },
      { text: "Plan the frame order. Frame 1 does most of the work — people skip fast", tag: "Frame plan" },
      { text: "Design inside the safe area. App buttons cover the top and bottom", tag: "Design" },
      { text: "Add the poll, question, link or countdown", tag: "Interactive" },
      { stop: "Self check, Division Lead or named project lead approves, client approves" },
      { text: "Publish, then save to Highlights if it is useful beyond today", tag: "Live + saved" },
    ],
    finishedWhen: "Stories are live, and saved to Highlights if they are evergreen.",
  },
  S5: {
    title: "Publishing — read this every time",
    before: [
      "Copy and design are in 04 Approved",
      "Client has approved, at least a week ago",
    ],
    steps: [
      { text: "Take the file from 04 Approved ONLY. Not from chat, not from someone's desktop", tag: "Right file" },
      { text: "CHECK 1 — is this the approved version, and does the copy match?", tag: "Version ok" },
      { text: "CHECK 2 — is the RIGHT CLIENT account selected?", tag: "Account ok" },
      { text: "CHECK 3 — is the date and time right?", tag: "Time ok" },
      { text: "CHECK 4 — is the platform and format right?", tag: "Format ok" },
      { text: "For anything big, get a second person to look before you schedule", tag: "Second check" },
      { text: "Schedule it in the tool", tag: "Scheduled" },
      { stop: "The client sees it scheduled and confirms. Nothing goes live with a question open" },
      { text: "Publish", tag: "Live" },
      { text: "WITHIN 15 MINUTES check the live post: is it there, does it look right, line breaks, tags, order, links, correct account", tag: "Checked live" },
      { text: "Log it in 05 Published", tag: "Logged" },
    ],
    finishedWhen: "The post is live, checked within 15 minutes, and logged. If anything is wrong, go to card S13 now.",
  },
  S6: {
    title: "Comments and messages (every day)",
    before: [
      "The client's reply rules are written down and approved",
    ],
    steps: [
      { text: "Check comments and messages at least TWICE a day", tag: "Checked" },
      { text: "Sort each one: question, complaint, praise, someone wanting to buy, spam, off topic", tag: "Sorted" },
      { text: "Reply using the client's rules. If it is not covered, ASK — do not make it up", tag: "Replied" },
      { text: "The first 48 hours after a post matter most — early replies get more reach", tag: "Priority done" },
      { text: "Anyone who wants to buy goes to card S7 the same day", tag: "Lead routed" },
      { text: "Write down anything you had to escalate, and what was decided", tag: "Logged" },
      { text: "Save the interesting comments for the monthly report", tag: "Saved" },
    ],
    finishedWhen: "Nothing is unanswered for more than one working day.",
  },
  S7: {
    title: "Someone wants to buy — from a comment or DM",
    before: [
      "You know where this client wants their leads sent",
    ],
    steps: [
      { text: "Spot it: asking price, asking if available, asking how to contact", tag: "Spotted" },
      { text: "Reply the same day even if the real answer has to come from the client", tag: "Replied" },
      { text: "Write down: name, handle, platform, what they asked, the full chat, the date", tag: "Lead details" },
      { text: "Send it to where the client wants it, WITHIN ONE WORKING DAY, with the full chat", tag: "Sent to client" },
      { text: "Log it in the lead sheet", tag: "Logged" },
    ],
    finishedWhen: "The client has the lead, with the full conversation, within one working day.",
  },
  S8: {
    title: "Ad creatives for the paid team",
    before: [
      "Brief from the paid team, at least 5 working days before launch",
      "You know what they are testing",
    ],
    steps: [
      { text: "Read the brief: where the ads run, exact sizes, how many versions, the offer, launch date", tag: "Brief clear" },
      { text: "Ask what they are TESTING. If you do not know, the versions will be useless", tag: "Test known" },
      { text: "Design ONE main ad at the most important size", tag: "Main ad" },
      { stop: "Project lead (if named), then Division Lead, then founders approve the main ad" },
      { text: "Make the versions FROM the approved main ad", tag: "Versions" },
      { text: "Change only ONE thing per version: the headline, OR the image, OR the offer", tag: "Clean test" },
      { text: "Make every size they asked for", tag: "All sizes" },
      { text: "Check each one: right size, text rules, safe edges, readable in a feed", tag: "Checked" },
      { text: "Name the files our way and send 3 working days before launch", tag: "Delivered" },
    ],
    finishedWhen: "Main ad plus all versions and sizes delivered, correctly named, 3 days before launch.",
  },
  S9: {
    title: "The calendar — monthly and weekly",
    before: [
      "Approved social strategy for the client",
      "Last month's numbers",
    ],
    steps: [
      { text: "Take the themes and how often to post from the approved strategy", tag: "Themes" },
      { text: "Look at what worked and what did not last month", tag: "Learnings" },
      { text: "Fill the month: date, platform, theme, format, topic, what it is for, what people should do", tag: "Month plan" },
      { text: "Mix it up. Not everything selling, not everything teaching", tag: "Balanced" },
      { text: "Write down EVERYTHING you need from another team NOW — video needs 2 weeks for a shoot and 5 working days for an edit", tag: "Requests raised" },
      { text: "Put a writer and a designer on each item, with 4 dates: copy, design, review, publish", tag: "Assigned" },
      { text: "Check the team actually has time for this", tag: "Capacity ok" },
      { stop: "Client approves the month" },
      { text: "Every week: confirm next week is fully approved BEFORE the week starts", tag: "Week ready" },
    ],
    finishedWhen: "Month approved, next week fully approved before it starts, and every other team knows what we need.",
  },
  S10: {
    title: "Campaign idea",
    before: [
      "Approved strategy",
      "You know the goal, the audience and the dates",
    ],
    steps: [
      { text: "Write the brief: goal, audience, main message, platforms, how long, how we will know it worked", tag: "Brief" },
      { text: "Look at what competitors did and collect reference visuals", tag: "References" },
      { text: "Make TWO or THREE ideas. Not more. More ideas means nobody can choose", tag: "2-3 routes" },
      { stop: "Project lead (if named), then Division Lead, then founders check the idea" },
      { stop: "Client picks ONE idea" },
      { text: "Show how that one idea works across every format and platform", tag: "Expanded" },
      { text: "List every asset to be made, in order, with a person and a date", tag: "Asset list" },
    ],
    finishedWhen: "One idea approved, with a dated asset list and a name against each item.",
  },
  S11: {
    title: "Brochure, PDF or deck",
    before: [
      "What it is for, who reads it, what must be in it, the deadline",
    ],
    steps: [
      { text: "Look at similar documents from competitors and collect layout references", tag: "References" },
      { text: "Write the page by page plan: what goes on each page, in what order", tag: "Page plan" },
      { stop: "Client approves the plan BEFORE anyone writes the copy" },
      { text: "Write the copy", tag: "Copy" },
      { stop: "Client approves the copy" },
      { text: "Design only the cover and ONE inside page (deck: one slide of each type)", tag: "Samples" },
      { stop: "Division Lead or named project lead approves the samples. THEN build the rest" },
      { text: "Build all pages", tag: "Full document" },
      { text: "Check: links work, page order, spelling, image quality, print bleed if it will be printed", tag: "Checked" },
      { stop: "Client approves it on a call. Do not just send it" },
      { text: "Export in every format they need. For decks, also give the editable file", tag: "Delivered" },
    ],
    finishedWhen: "Final document delivered in every format, with the editable file for decks.",
  },
  S12: {
    title: "Landing page",
    before: [
      "The offer, who it is for, the deadline, what tracking is needed",
    ],
    steps: [
      { text: "Look at competitor pages and collect references", tag: "References" },
      { text: "Draw a rough page layout with no design", tag: "Wireframe" },
      { stop: "Client approves the OFFER, the MESSAGE and the PAGE ORDER. This is the big one" },
      { text: "Write the plan: sections, forms, buttons, what connects to what, who does what, dates", tag: "Build plan" },
      { text: "Tell Tech and Automation now, through your Division Lead, what tracking, domain and connections are needed. They need 5 working days", tag: "Raised" },
      { text: "Write the copy: headlines, body, buttons, FAQs, proof", tag: "Copy" },
      { stop: "Project lead (if named), then Division Lead, then founders approve the copy" },
      { text: "Design for desktop AND mobile", tag: "Design" },
      { stop: "Project lead (if named), then Division Lead, then founders approve the design" },
      { text: "Build it in Lovable", tag: "Page built" },
      { text: "Test everything: all screen sizes, speed, fill the form yourself, every button, every link, tracking fires", tag: "Tested" },
      { stop: "Client approves on the live preview" },
      { text: "Publish, then test the form and buttons AGAIN on the live page", tag: "Live + verified" },
      { text: "Tell the paid team the page has passed checks — they cannot run ads before this", tag: "Paid team told" },
    ],
    finishedWhen: "Page is live, forms work, tracking works, and the paid team has been told it passed.",
  },
  S13: {
    title: "Something wrong went live",
    before: [
      "Nothing. Do this the moment you notice",
    ],
    steps: [
      { text: "Tell the Division Lead RIGHT NOW. Not the group chat. Not later", tag: "Told" },
      { text: "In 5 minutes work out: what is wrong, is it damaging, has anyone seen or shared it", tag: "Assessed" },
      { stop: "Division Lead or named project lead decides: take it down, or correct it in a follow up" },
      { text: "Founders and client are told within the hour. They hear it from US first", tag: "Informed" },
      { text: "Take it down, then check everywhere else: other accounts, anything still scheduled", tag: "All clear" },
      { text: "Within 24 hours write down WHICH CHECK FAILED — approval, wrong folder, wrong account, or client review", tag: "Cause found" },
      { text: "Fix the check, not the person", tag: "Fixed" },
    ],
    finishedWhen: "It is down, everyone who needed to know knows, and the failed check has been fixed.",
  },
  F1: {
    title: "Client setup — do this BEFORE any work",
    before: [
      "A new client, or a client where something below is missing",
    ],
    steps: [
      { text: "Brand guidelines, logo, colours and fonts exist", tag: "Brand files" },
      { text: "An approved social strategy exists: themes, platforms, how often, what success looks like", tag: "Strategy" },
      { text: "We have admin access on every platform", tag: "Access" },
      { text: "The accounts are set up properly: bio, picture, links, contact buttons", tag: "Setup done" },
      { text: "Reply rules are written and the client has agreed who replies: us or them", tag: "Reply rules" },
      { text: "We know WHERE to send a lead that comes from a comment or DM", tag: "Lead destination" },
      { text: "ONE named person at the client approves our work, and we know the ONE channel where their approvals are written down", tag: "Approver named" },
      { text: "The client's dos and don'ts are written down: what we must never say, show or claim", tag: "Dos and don'ts" },
      { text: "Their tone of voice is written down. If it does not exist, writing it becomes a job", tag: "Tone of voice" },
      { text: "IF ANYTHING IS MISSING: tell the Division Lead. It becomes a job with a date. Do not work around it", tag: "Raised" },
    ],
    finishedWhen: "All six exist and are saved in the client's Foundation folder.",
  },
};
